import styled from "@emotion/styled";
import { useQuery } from "@tanstack/react-query";
import { useWindowSize } from "@reactuses/core";
import { useMemo, useState } from "react";
import * as XLSX from "xlsx";
import {
  AnalysisEventListRow,
  AnalysisFilter,
  EventListCategory,
  getAnalysisEventList,
} from "../../api/analysis";
import { readShopZoneError } from "../../api/shopZone";
import { ModalLayout } from "../../components/common/ModalLayout";
import theme from "../../theme";
import { EVENT_TYPES, fmtInt, fmtMinutes } from "./analysisUtil";

/**
 * 처리 결과별 이벤트 목록 (차량전복 처리 결과 항목을 눌렀을 때).
 * 건수는 요약 KPI 와 같은 조건으로 센다. 줄마다 원시 데이터를 열 수 있다.
 */

const { md } = theme.screen;
const STATUS_LABEL: Record<number, string> = { 0: "미처리", 1: "취소", 2: "해제" };

const COLS: { label: string; value: (r: AnalysisEventListRow, typeName: (c: number | null) => string) => string | number }[] = [
  { label: "발생 일시", value: (r) => r.dt },
  { label: "유형", value: (r) => EVENT_TYPES[r.type]?.label ?? r.type },
  { label: "차량", value: (r) => r.vhcl_nm ?? "" },
  { label: "차종", value: (r, tn) => tn(r.type_cd) },
  { label: "단말", value: (r) => r.dvc_sn ?? "" },
  { label: "소속", value: (r) => r.ogdp_nm ?? "" },
  { label: "속도(km/h)", value: (r) => (r.speed === null || r.speed === undefined ? "" : Number(r.speed)) },
  { label: "처리", value: (r) => (r.status === null ? "" : STATUS_LABEL[r.status] ?? String(r.status)) },
  { label: "해제 사유", value: (r) => r.rmv_rsn ?? "" },
  { label: "해제 일시", value: (r) => r.rmv_dt ?? "" },
  { label: "처리 시간(분)", value: (r) => (r.handle_min === null || r.handle_min === undefined ? "" : Number(r.handle_min)) },
  { label: "주소", value: (r) => r.addr ?? "" },
];

interface Props {
  filter: AnalysisFilter;
  /** null 이면 닫힘 */
  category: EventListCategory | null;
  title: string;
  color?: string;
  typeName: (code: number | null) => string;
  onClose: () => void;
  onOpenRaw: (id: number) => void;
}

export default function EventListModal({ filter, category, title, color, typeName, onClose, onOpenRaw }: Props) {
  const { width } = useWindowSize();
  const isMobile = width <= md;
  const [q, setQ] = useState("");
  const list = useQuery({
    queryKey: ["analysis", "list", filter, category],
    queryFn: () => getAnalysisEventList(filter, category as EventListCategory),
    enabled: category !== null,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const rows = useMemo(() => {
    const all = list.data ?? [];
    const k = q.trim().toLowerCase();
    if (!k) return all;
    return all.filter((r) =>
      [r.vhcl_nm, r.dvc_sn, r.ogdp_nm, r.rmv_rsn, r.addr, r.dt].some((v) => (v ?? "").toLowerCase().includes(k))
    );
  }, [list.data, q]);

  const download = () => {
    const aoa = [COLS.map((c) => c.label), ...rows.map((r) => COLS.map((c) => c.value(r, typeName)))];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!cols"] = COLS.map((c) => ({ wch: c.label === "주소" || c.label === "해제 사유" ? 36 : c.label.includes("일시") ? 20 : 14 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "목록");
    XLSX.writeFile(wb, `${title.replace(/[\\/:*?"<>|]/g, "")}_${filter.startDate}_${filter.endDate}.xlsx`);
  };

  return (
    <ModalLayout
      open={category !== null}
      onClose={() => {
        setQ("");
        onClose();
      }}
      width="1200px"
      title={title}
      pt="60px"
      pb="24px"
      fullScreen={isMobile}
    >
      <Body>
        <Toolbar>
          <span className="count">
            {color && <i style={{ background: color }} />}
            {filter.startDate} ~ {filter.endDate} · {fmtInt(list.data?.length ?? 0)}건
            {q.trim() && ` 중 ${fmtInt(rows.length)}건`}
          </span>
          <input
            type="search"
            placeholder="차량·단말·소속·사유·주소 검색"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <span className="spacer" />
          <button type="button" className="excel" disabled={!rows.length} onClick={download}>
            엑셀 다운로드
          </button>
        </Toolbar>

        {list.isLoading && <p className="note">목록을 불러오는 중…</p>}
        {list.error && <p className="note bad">불러오지 못했다 — {readShopZoneError(list.error).message}</p>}
        {list.data && !rows.length && <p className="note">해당하는 건이 없다.</p>}

        {rows.length > 0 && (
          <TableWrap>
            <table>
              <thead>
                <tr>
                  <th>원시 데이터</th>
                  {COLS.map((c) => (
                    <th key={c.label}>{c.label === "처리 시간(분)" ? "처리 시간" : c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <button type="button" className="raw" onClick={() => onOpenRaw(r.id)}>
                        보기
                      </button>
                    </td>
                    <td>{r.dt}</td>
                    <td style={{ color: EVENT_TYPES[r.type]?.color }}>{EVENT_TYPES[r.type]?.label ?? r.type}</td>
                    <td className="l">{r.vhcl_nm ?? "-"}</td>
                    <td className="l">{typeName(r.type_cd) || "-"}</td>
                    <td>{r.dvc_sn ?? "-"}</td>
                    <td className="l">{r.ogdp_nm ?? "-"}</td>
                    <td className="r">{r.speed ?? "-"}</td>
                    <td>{r.status === null ? "-" : STATUS_LABEL[r.status] ?? r.status}</td>
                    <td className="l wrap">{r.rmv_rsn ?? "-"}</td>
                    <td>{r.rmv_dt ?? "-"}</td>
                    <td className="r">{r.handle_min === null || r.handle_min === undefined ? "-" : fmtMinutes(r.handle_min)}</td>
                    <td className="l wrap">{r.addr ?? "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        )}
        {(list.data?.length ?? 0) >= 2000 && <p className="note">최근 2,000건만 보인다. 기간을 줄여 조회한다.</p>}
      </Body>
    </ModalLayout>
  );
}

const Body = styled.div`
  width: min(1200px, calc(100vw - 80px));
  padding: 0 4px 4px;
  font-size: 13px;
  color: ${theme.color.neutral_800};
  .note {
    margin: 8px 0;
    font-size: 12px;
    color: ${theme.color.neutral_500};
  }
  .note.bad {
    color: ${theme.color.red_600};
  }
  @media (max-width: ${md}px) {
    width: auto;
    padding: 0 12px 12px;
  }
`;

const Toolbar = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 10px;
  .count {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
  }
  .count i {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 2px;
  }
  input[type="search"] {
    height: 30px;
    min-width: 220px;
    border: 1px solid ${theme.color.neutral_300};
    border-radius: 6px;
    padding: 0 8px;
    font-size: 12.5px;
  }
  .spacer {
    flex: 1 1 auto;
  }
  .excel {
    font-size: 12px;
    padding: 6px 12px;
    border-radius: 6px;
    border: 1px solid ${theme.color.green_500};
    background: ${theme.color.green_500};
    color: #fff;
    cursor: pointer;
  }
  .excel:disabled {
    opacity: 0.4;
    cursor: default;
  }
  @media (max-width: ${md}px) {
    input[type="search"] {
      min-width: 0;
      flex: 1 1 160px;
    }
  }
`;

const TableWrap = styled.div`
  max-height: min(560px, calc(100vh - 220px));
  overflow: auto;
  border: 1px solid ${theme.color.neutral_100};
  border-radius: 8px;
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
  th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: #f3f4f6;
    font-weight: 600;
    padding: 7px 8px;
    border-bottom: 1px solid ${theme.color.neutral_200};
  }
  td {
    padding: 6px 8px;
    border-bottom: 1px solid #f0f1f3;
    text-align: center;
  }
  td.l {
    text-align: left;
  }
  td.r {
    text-align: right;
  }
  td.wrap {
    white-space: normal;
    min-width: 160px;
    max-width: 280px;
  }
  tbody tr:hover td {
    background: #f9fafb;
  }
  .raw {
    font-size: 12px;
    padding: 3px 10px;
    border-radius: 4px;
    border: 1px solid ${theme.color.green_500};
    background: #fff;
    color: ${theme.color.green_500};
    cursor: pointer;
  }
  .raw:hover {
    background: ${theme.color.green_500};
    color: #fff;
  }
`;
